<script setup lang="ts">
import type { IPlayerState } from "@pixellot/vue-player/src/util/state";
import type { VideoJsPlayer as IPixellotPlayer } from "@pixellot/web-sdk";
import { VideoClipper } from "@pixellot/vue-player";
import { tagsMap, THEME_COLOR } from "~/constants";
import useAsync from "~/hooks/use-async";
import TagsAPI from "~/services/tags";
import useEventStore from "~/stores/event";
import useUserStore from "~/stores/user";
import getErrorLocalePathMessage from "~/utils/locale";
import { formatTime } from "~/utils/time";
import { capitalize } from "~/utils/typography";

export interface CreateClipModalProps {
  mediaItem?: IModelEvent | null;
}

const props = defineProps<CreateClipModalProps>();

const { t, te } = useI18n();
const color = useColorMode();
const toaster = useToast();
const userStore = useUserStore();
const eventStore = useEventStore();

const emit = defineEmits(["close"]);
const closeModal = () => {
  emit("close");
  eventStore.tags.isCreatingTag = false;
  eventStore.tags.isEditingTag = false;
  eventStore.tags.activeEditTag = null;
};
const editTag = computed(() => eventStore.tags.activeEditTag);
const canCreateGlobalTag = computed(() =>
  Boolean(userStore.user?.canCreateGlobalTag),
);
const tagsOptions = computed(() => {
  const sportType = props.mediaItem?.sportType;
  const tags = sportType && tagsMap[sportType] ? tagsMap[sportType] : tagsMap.general;

  return tags.map(tag => ({
    id: tag.id,
    title: t(`tags.${tag.id}`),
  }));
});

const onError = (error: Error) => {
  const { message } = getErrorLocalePathMessage(error.message, { t, te });
  return toaster.error(message) && closeModal();
};
const onSuccess = () => toaster.success(editTag.value
  ? t("pages.events.tabs.event_tags.tag_updating_successfully_text")
  : t("pages.events.tabs.event_tags.tag_creating_successfully_text"),
) && closeModal();
const { loading: createLoading, run: createTag } = useAsync(TagsAPI.save, { onSuccess, onError });
const { loading: updateLoading, run: updateTag } = useAsync(TagsAPI.update, { onSuccess, onError });
const loading = computed(() => createLoading.value || updateLoading.value);

const teamOptions = reactive<Required<IModelTag["tagTeam"][]>>([
  {
    teamId: props.mediaItem?.team1Id,
    title: props.mediaItem?.team1Name,
    color: props.mediaItem?.team1Color,
  },
  {
    teamId: props.mediaItem?.team2Id,
    title: props.mediaItem?.team2Name,
    color: props.mediaItem?.team2Color,
  },
]);

const tagData = reactive({
  id: editTag.value?.id || "",
  title: editTag.value?.title || capitalize(tagsOptions.value[0].title),
  type: editTag.value?.type || tagsOptions.value[0].id,
  startTime: editTag.value?.startTime || 0,
  endTime: editTag.value?.endTime || 0,
  playerNumber: editTag.value?.playerNumber || "",
  global: editTag.value?.global || false,
  tagTeam: editTag.value?.tagTeam,
} as IModelTag);

const onTagSelect = (tag: { id: string; title: string }) => {
  tagData.type = tag.id as TTagType;

  if ((!tagData.title || tagData.title !== capitalize(tag.title)) && !editTag.value)
    tagData.title = capitalize(tag.title);
};
const onFormSubmit = () => {
  if (editTag.value) {
    updateTag(tagData, props.mediaItem);
    return;
  }

  createTag(tagData, props.mediaItem);
};

const player = computed<IPixellotPlayer | null>(() => eventStore.player ? eventStore.player : null);
const playerState = computed<IPlayerState | null>(() => eventStore.playerState ? eventStore.playerState : null);
const videoClipperRef = ref<InstanceType<typeof VideoClipper> | null>(null);

const selectedDuration = computed(() => {
  if (!videoClipperRef.value || !videoClipperRef.value.selectedRange || !eventStore.player)
    return 0;

  const [rangeStart, rangeEnd] = videoClipperRef.value.selectedRange;
  const duration = rangeEnd - rangeStart;

  return Math.ceil(duration);
});

watch(() => videoClipperRef.value?.selectedRange, (range) => {
  if (!range)
    return;
  tagData.startTime = Math.floor(range[0]);
  tagData.endTime = Math.floor(range[1]);
});

onMounted(() => {
  const currentTime = eventStore.player?.currentTime() || 0;
  const timeMargin = 60;
  const start = currentTime - timeMargin;
  const end = currentTime + timeMargin;

  const startTagTime = tagData.startTime;
  const endTagTime = tagData.endTime;

  nextTick(() => {
    if (editTag.value) {
      eventStore.player?.trimAPI.setRange(startTagTime, endTagTime);
      videoClipperRef.value?.setRange(startTagTime, endTagTime);

      return;
    }

    if (player.value) {
      player.value?.trimAPI.setRange(start, end);
      videoClipperRef.value?.setRange(currentTime, end);
    }
  });
});
onBeforeUnmount(() => eventStore.player?.trimAPI?.hide());
</script>

<template>
  <div class="createTagModal">
    <ClientOnly>
      <Teleport to="#player-bottom">
        <div v-if="eventStore.tags.isCreatingTag || eventStore.clips.isCreatingClip" class="flex flex-col background-main">
          <div class="flex items-center justify-center gap-4 my-4 text-secondary">
            <div class="border min-w-[5rem] px-4 py-1 rounded border-[var(--base-card-bgSecondaryColor)]">
              {{ formatTime(tagData.startTime) }}
            </div>
            <span>-</span>
            <div class="border min-w-[5rem] px-4 py-1 rounded border-[var(--base-card-bgSecondaryColor)]">
              {{ formatTime(tagData.endTime) }}
            </div>
          </div>
          <VideoClipper
            v-if="player && playerState"
            ref="videoClipperRef"
            :player="player"
            :player-state="playerState"
          />

          <div class="my-4 text-sm font-medium text-center text-secondary">
            {{ selectedDuration }} {{ t('labels.seconds', 0) }}
          </div>
        </div>
      </Teleport>
    </ClientOnly>

    <div class="createTagModal__container px-4">
      <div class="flex items-center justify-between pt-4">
        <h2 class="text-[var(--base-tab-text-color)] text-heading-3 text-center">
          {{ editTag ? t("pages.events.tabs.event_tags.update_tag") : t("pages.events.tabs.event_tags.create_tag") }}
        </h2>

        <BaseButton
          type="icon"
          name="modal-close"
          :title="t('button.cancel')"
          @click.prevent="closeModal()"
        >
          <IconClose />
        </BaseButton>
      </div>

      <form class="h-[calc(100%-220px)]" @submit.prevent="onFormSubmit">
        <div class="h-[calc(100%-220px)] w-full mt-5">
          <div class="text-heading-4 text-secondary mb-2">
            {{ t("pages.events.tabs.event_tags.choose_tag") }}
          </div>
          <div class="baseTagSelect">
            <EventTagsSelect
              v-for="tag in tagsOptions"
              :key="tag.id"
              :tag-type="tag.id"
              :tag-title="tag.title"
              :is-active="tagData.type === tag.id"
              @click="onTagSelect(tag)"
            />
          </div>
          <BaseInput
            v-model="tagData.title"
            :label="t('pages.events.tabs.event_tags.name_the_tag')"
            label-class="text-heading-4"
            :placeholder="t('labels.tag_name_placeholder')"
            type="text"
            name="tag-modal"
            root-class="mt-6"
            required
            outline
          />
          <div class="tagModalInput">
            <div class="mt-6">
              <label
                id="input-label-teamColor"
                class="inline-block text-heading-4 text-secondary transition"
              >
                {{ t("pages.events.tabs.event_tags.choose_player_color") }}
              </label>
            </div>
            <div class="baseInputGroup">
              <BaseInput
                v-model="tagData.playerNumber"
                label-class="text-heading-4"
                type="text"
                pattern="[0-9]{0,2}"
                aria-labelledby="input-label-teamColor"
                name="tag-modal"
                outline
                root-class="w-40"
              >
                <template #prefix>
                  <span class="text-secondary"> # </span>
                </template>
              </BaseInput>
              <BaseSelect
                v-model="tagData.tagTeam"
                :options="teamOptions"
                name="teamInfo"
                :placeholder="t('pages.events.tabs.event_tags.choose_team')"
                options-key-value="title"
                root-class="w-full flex-grow"
              >
                <template #innerInputPrefix>
                  <div>
                    <div
                      class="rounded-full h-4 w-4 flex"
                      :style="{ 'background-color': tagData.tagTeam?.color as string }"
                    />
                  </div>
                </template>
                <template #option-item-inner="{ option, select }">
                  <li class="baseSelect__listOption flex !justify-start gap-2" @click="select(option)">
                    <div>
                      <div
                        class="rounded-full h-4 w-4 flex"
                        :class="{ border: color.value === THEME_COLOR.LIGHT }"
                        :style="{ 'background-color': (option as IModelIdentityItemTag)?.color as string }"
                      />
                    </div>
                    <div class="font-medium truncate w-full lg:max-xl:w-32">
                      {{ (option as IModelIdentityItemTag)?.title as string }}
                    </div>
                  </li>
                </template>
              </BaseSelect>
            </div>
          </div>

          <BaseCheckbox
            v-if="canCreateGlobalTag"
            v-model="tagData.global"
            name="global-tag"
            class="text-main mt-5"
            :label="t('pages.events.tabs.event_tags.make_tag_global')"
          />
          <div class="flex w-full justify-between gap-2">
            <BaseButton
              type="secondary"
              name="modal-close"
              :title="t('button.cancel')"
              class="w-1/2 text-button-bold text-main my-6"
              @click="closeModal()"
            >
              {{ t("button.cancel") }}
            </BaseButton>
            <BaseButton
              type="primary"
              name="create-tag"
              :disabled="loading"
              :title="editTag ? t('pages.events.tabs.event_tags.update_tag') : t('pages.events.tabs.event_tags.create_tag') "
              class="w-1/2 text-button-bold text-main my-6"
            >
              <div class="flex items-center justify-center gap-2">
                <IconTag
                  :width="16"
                  :height="16"
                  fill="#ffffff"
                />
                <span class="truncate">
                  {{ editTag ? t("pages.events.tabs.event_tags.update_tag") : t("pages.events.tabs.event_tags.create_tag") }}
                </span>
              </div>
            </BaseButton>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="scss">
//FIXME: changed styles;
.createTagModal {
  @apply h-full w-full lg:aspect-[1];

  .createTagModal__container {
    @apply h-full;
    background: var(--base-card-bgColor);

    .baseTagSelect {
      @apply overflow-auto w-full flex flex-wrap gap-2 md:max-h-[12rem] lg:max-h-[8rem] 2xl:max-h-[20rem];
    }

    .tagModalInput {
      @apply relative;

      .baseSelect {
        > .baseInput .baseInput__inner {
          background-color: var(--base-card-bgSecondaryColor);
        }
      }
    }
  }
}
</style>
